type TronRequest = (payload: {
  method: string
}) => Promise<{ code?: number } | unknown>

type TronProviderWindow = Window & {
  ethereum?: {
    selectedAddress?: string | null
    providers?: Array<{ selectedAddress?: string | null }>
  }
  tronLink?: {
    ready?: boolean
    request?: TronRequest
  }
  tronWeb?: {
    defaultAddress?: { base58?: string; hex?: string }
    ready?: boolean
    request?: TronRequest
    contract: () => {
      at: (address: string) => Promise<{
        approve: (
          spender: string,
          amount: string,
        ) => { send: () => Promise<unknown> }
        allowance: (
          owner: string,
          spender: string,
        ) => { call: () => Promise<{ toString: () => string } | string> }
      }>
    }
    address: { toHex: (base58: string) => string }
  }
  imToken?: unknown
}

export function getTronProviderWindow(): TronProviderWindow | undefined {
  if (typeof window === "undefined") return undefined
  return window as TronProviderWindow
}

export function readTronAddress(): string | null {
  const base58 = getTronProviderWindow()?.tronWeb?.defaultAddress?.base58
  return typeof base58 === "string" && base58.length > 0 ? base58 : null
}

/** True when any TRON injection is present (TronLink, imToken, Trust, etc.). */
export function hasTronInjection(): boolean {
  const w = getTronProviderWindow()
  if (!w) return false
  return Boolean(w.tronLink || w.tronWeb)
}

function readEvmSelectedAddress(): string | null {
  const eth = getTronProviderWindow()?.ethereum
  if (!eth) return null
  const candidates = [
    eth.selectedAddress,
    ...(Array.isArray(eth.providers)
      ? eth.providers.map((provider) => provider.selectedAddress)
      : []),
  ]
  for (const value of candidates) {
    if (typeof value === "string" && /^0x[a-fA-F0-9]{40}$/.test(value)) {
      return value
    }
  }
  return null
}

/**
 * Prefer TRON connect when the page is clearly in a Tron DApp browser
 * (tronWeb ready / Tron address present) and no usable EVM account is available.
 * This prevents wagmi/viem from validating Address "null" inside imToken/Trust TRON browsers.
 */
export function prefersTronEnvironment(): boolean {
  if (readTronAddress()) return true

  const w = getTronProviderWindow()
  if (!w) return false

  const hasTron = Boolean(w.tronLink || w.tronWeb)
  if (!hasTron) return false

  if (w.tronWeb?.ready) return !readEvmSelectedAddress()

  // Injected Tron without a valid EVM account → Tron-first session
  return !readEvmSelectedAddress()
}

async function waitForTronAddress(timeoutMs = 8_000): Promise<string> {
  const started = Date.now()
  while (Date.now() - started < timeoutMs) {
    const address = readTronAddress()
    if (address) return address
    await new Promise((resolve) => setTimeout(resolve, 250))
  }
  throw new Error("Tron wallet connection timed out")
}

/**
 * Request TRON account authorization.
 * Supports TronLink (`tronLink.request`), imToken/Trust (`tronWeb` already injected
 * or `tronWeb.request`), and DApp browsers that expose a ready address.
 */
export async function requestTronAccounts(): Promise<string> {
  const w = getTronProviderWindow()
  if (!w?.tronLink && !w?.tronWeb) {
    throw new Error("Tron wallet not found")
  }

  const existing = readTronAddress()
  if (existing) return existing

  // Trust Wallet's request implementation accesses private fields through
  // `this`; invoking an extracted function loses that receiver.
  if (w.tronLink?.request) {
    await w.tronLink.request.call(w.tronLink, {
      method: "tron_requestAccounts",
    })
  } else if (w.tronWeb?.request) {
    await w.tronWeb.request.call(w.tronWeb, {
      method: "tron_requestAccounts",
    })
  }

  return waitForTronAddress()
}
